<template>
    <div class="activities-listing-container">
    <ListingWrapper
        :title="$t.__('common.labels.activities')"
        :loading="loadingActivitiesPage"
        :isTableLoading="isTableLoading"
        class="activities-listing"
        @update-pagination="handleUpdatePagination"
    >
        <template #listingTabs>
            <TabsNav
                v-if="!!listingTabs.length"
                :tabs="listingTabs"
                :activeTab="activeTab"
                @tab-clicked="(tab) => handleSelectedTab(tab)"
            />
        </template>
        <template #settings>
            <ActivitiesListingSettingsHeader
                ref="ActivitiesTable"
                :columns="columns"
                :activeTab="activeTab"
                @updateColumns="handleUpdateColumns"
            />
        </template>
        <template #listingActions>
            <CompassMenu v-if="canExport" customClass="activities-listing-export-menu">
                <CompassButton :label="$t.__('common.actions.export')" :type="CompassEnums.ButtonType.SECONDARY" dropdown />
                <template #dropdown>
                    <CompassMenuItem
                        :label="$t.__('common.labels.export_csv')"
                        @click="handleExport('csv')"
                    />
                    <CompassMenuItem
                        :label="$t.__('common.labels.export_pdf')"
                        @click="handleExport('pdf')"
                    />
                    <CompassMenuItem
                        :label="$t.__('common.labels.export_advanced')"
                        @click="handleExport('advanced')"
                    />
                </template>
            </CompassMenu>
            <CompassMenu>
                <CompassButton :label="$t.__('common.actions.create_activity')" dropdown />
                <template #dropdown>
                    <CompassMenuItem
                        :label="$t.__('common.labels.task')"
                        @click="handleCreateActivity('task')"
                    >
                        <template #prefixIcon>
                            <CompassIcon size="large" icon="obj-slsy-task" />
                        </template>
                    </CompassMenuItem>
                </template>
            </CompassMenu>
        </template>
        <template #table>
            <ActivitiesListingTable :isActivityPage="true" :columns="columns" />
        </template>
    </ListingWrapper>
    <ExportModal
        v-if="display.export"
        listinghash="activitiesStores"
        :exportType="display.exportType"
    />
    </div>
</template>

<script>
import ListingWrapper from 'vcomponents/common/listing/ListingWrapper.vue';
import ActivitiesListingTable from 'vcomponents/activities/listing/ActivitiesListingTable.vue';
import ActivitiesListingSettingsHeader from 'vcomponents/activities/listing/ActivitiesListingSettingsHeader.vue';
import TabsNav from 'vcomponents/utilities/TabsNav.vue';
import ExportModal from 'vcomponents/listing/ExportModal.vue';
import ActivitiesStore from 'stores/activitiesStore.js';
import ListingSettingsStore from 'stores/listingSettingsStore.js';
import ActivitiesTableColumnsMixins from 'mixins/activities/ActivitiesTableColumnsMixins.js';
import { TABS } from 'Dictionaries/activities/ActivitiesEnums.js';
import { createNamespacedHelpers } from 'vuex';

const { mapGetters: listingSettingsGetters, mapActions: listingSettingsActions } =
    createNamespacedHelpers('listingSettingsStores');
const popupPanelMapActions = createNamespacedHelpers('popupPanel').mapActions;
const { mapActions, mapGetters } = createNamespacedHelpers('activitiesStores');
const { mapGetters: userGetters } = createNamespacedHelpers('user');

export default {
    components: {
        ActivitiesListingTable,
        ActivitiesListingSettingsHeader,
        ListingWrapper,
        TabsNav,
        ExportModal,
    },
    mixins: [ActivitiesTableColumnsMixins],
    computed: {
        ...mapGetters({
            loadingActivitiesPage: 'getLoadingActivitiesPage',
            isTableLoading: 'getLoadingTable',
            activities: 'getActivities',
            totalActivities: 'getTotalActivitiesInDB',
            isLoadingMore: 'getLoadingMore',
            filters: 'getFilters',
            activeTab: 'getActiveTab',
            searchParams: 'getSearchParams',
            metas: 'getMetas',
            display: 'getDisplay',
        }),
        ...userGetters(['getCurrentUserId']),
        ...listingSettingsGetters({ columns: 'getColumns', pagination: 'getPagination' }),
        canExport() {
            return this.metas?.permissions?.export ?? false;
        },
        listingTabs() {
            return [
                { id: TABS.LATE, label: this.$t.__('activities.listing.tab_late') },
                { id: TABS.TODO, label: this.$t.__('activities.listing.tab_todo') },
                { id: TABS.DONE, label: this.$t.__('activities.listing.tab_done') },
            ];
        },
        completed() {
            return this.activeTab === TABS.DONE;
        },
    },
    async created() {
        if (!this.$store.state.listingSettingsStores) {
            this.$store.registerModule('listingSettingsStores', ListingSettingsStore);
        }
        if (!this.$store.state.activitiesStores) {
            this.$store.registerModule('activitiesStores', ActivitiesStore);
        }
        await this.$store.dispatch('listingSettingsStores/initListingSettingsStore', {
            listingName: 'activities-listing-page:columns',
            defaultColumns: this.defaultColumns,
        });

        const persistedSelectedTab =
            localStorage.getItem(`activities-listing:tab-selected_${this.getCurrentUserId}`) ??
            TABS.TODO;
        await this.updateActiveTab(persistedSelectedTab);

        await this.$store.dispatch('activitiesStores/initActivityStore');
    },
    mounted() {
        EventBus.on('refresh_event', this.refreshActivities);
    },
    beforeUnmount() {
        EventBus.off('refresh_event');
        this.$store.unregisterModule('listingSettingsStores');
        this.$store.unregisterModule('activitiesStores');
    },
    methods: {
        ...mapActions([
            'initActivityStore',
            'updateFilters',
            'fetchActivitiesCrm',
            'clearActivities',
            'updateSearchParams',
            'refreshActivities',
            'updateLoadingMore',
            'updateLoadingTable',
            'updateActiveTab',
        ]),
        ...popupPanelMapActions(['open']),
        ...listingSettingsActions({ updateStoreColumns: 'updateColumns' }),
        async handleSelectedTab(selectedTab) {
            const oldTab = this.activeTab;
            if (this.isTableLoading) {
                return;
            }

            await this.updateLoadingTable(true);
            await this.updateActiveTab(selectedTab);

            let filters = { ...this.filters, completed: this.completed };
            if (this.activeTab === TABS.LATE) {
                // we have to remove due_date filter stored on localStorage when we switch to late tab
                let filtersFromStorage = JSON.parse(
                    localStorage.getItem(`activities-listing:filters_${this.getCurrentUserId}`),
                );
                if (filtersFromStorage) {
                    filtersFromStorage = filtersFromStorage.filter(
                        ({ code }) => code !== 'due_date',
                    );
                    localStorage.setItem(
                        `activities-listing:filters_${this.getCurrentUserId}`,
                        JSON.stringify(filtersFromStorage),
                    );
                }

                filters = { ...filters, due_date: { end: moment().toISOString() } };
            } else if (oldTab === TABS.LATE) {
                delete filters.due_date;
            }
            await this.updateFilters(filters);

            EventBus.emit('activities-listing-table:updateSortDirection');
        },
        handleUpdateColumns(newColumns) {
            setTimeout(() => {
                this.updateStoreColumns(newColumns);
            }, 0);
        },
        handleExport(format) {
            this.$store.commit('activitiesStores/toggleDisplay', 'export');
            this.$store.commit('activitiesStores/updateExportType', format);
        },
        handleCreateActivity(activityType) {
            this.open({
                key: 'activityFormPanel',
                props: {
                    id: null,
                    related: null,
                    activityType,
                    callback: () => {
                        this.refreshActivities();
                    },
                },
            });
        },
        async handleUpdatePagination(pagination) {
            await this.updateSearchParams(pagination);
            await this.clearActivities();
            await this.fetchActivitiesCrm();
        },
    },
};
</script>

<style lang="scss">
.activities-listing-export-menu.compass-menu__dropdown__container {
    z-index: 5;
}
</style>

<style lang="scss" scoped>
.activities-listing-container {
    position: relative;
    height: 100%;
}
</style>
